import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { Box, Typography, TextField, Button, Alert, Paper } from '@mui/material';
import LogTheme from './LogTheme';
import { useAuth } from '../../contexts/AuthContext';
import { useLogbook } from '../../contexts/LogbookContext';
import { getAuthHeaders } from '../../utils/api';

export default function JoinLogbook() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { isLoggedIn } = useAuth();
  const { refreshLogbooks, setCurrentLogbookId } = useLogbook();
  const codeFromUrl = searchParams.get('code') || '';
  const [code, setCode] = useState(codeFromUrl);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (codeFromUrl) setCode(codeFromUrl);
  }, [codeFromUrl]);

  useEffect(() => {
    if (!isLoggedIn) {
      const redirect = `${location.pathname}${location.search || ''}`;
      navigate(`/info?redirect=${encodeURIComponent(redirect)}`, { replace: true });
    }
  }, [isLoggedIn, navigate, location.pathname, location.search]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    if (!code || !code.trim()) {
      setError('Ange inbjudningskoden.');
      return;
    }
    setSubmitting(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_SERVER_URL}/api/invitations/accept`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ invitationCode: code.trim() })
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok) {
        setSuccess(true);
        await refreshLogbooks();
        const logbookId = data.invitation?.logbookId;
        if (logbookId) setCurrentLogbookId(logbookId);
        setTimeout(() => navigate('/info', { replace: true }), 2000);
      } else {
        setError(data.message || 'Kunde inte gå med i loggboken.');
      }
    } catch (err) {
      setError('Något gick fel.');
    } finally {
      setSubmitting(false);
    }
  };

  // Do not render invitation form or any content when logged out; redirect runs in useEffect
  if (!isLoggedIn) {
    return null;
  }

  return (
    <LogTheme>
      <Box sx={{ maxWidth: 600, mx: 'auto', p: { xs: 2, md: 4 } }}>
        <Typography variant="h1" gutterBottom>
          Gå med i en loggbok
        </Typography>
        <Typography color="text.secondary" sx={{ mb: 3 }}>
          Ange inbjudningskoden du fått via e-post eller av loggbokens ägare.
        </Typography>
        {success && (
          <Alert severity="success" sx={{ mb: 2 }}>
            Du har gått med i loggboken. Omdirigerar...
          </Alert>
        )}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
        <Paper sx={{ p: 3 }}>
          <form onSubmit={handleSubmit}>
            <TextField
              fullWidth
              label="Inbjudningskod"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              placeholder="t.ex. abc123..."
              disabled={submitting || success}
              sx={{ mb: 2 }}
            />
            <Button
              type="submit"
              variant="contained"
              color="primary"
              disabled={submitting || success}
            >
              Gå med
            </Button>
          </form>
        </Paper>
        <Button sx={{ mt: 2 }} onClick={() => navigate('/')}>
          Tillbaka till startsidan
        </Button>
      </Box>
    </LogTheme>
  );
}
